import React from 'react';
import { Card, CardMedia, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import fallbackImage from '../../../../../../assets/images/fallback/colocation_medium.png';
import { useLocale } from '@/hooks/use-locale';

// Add placeholder image constant at the top
const placeholderImage = fallbackImage;

interface HouseCardProps {
  image: string;
  title: string;
  availableDate: string;
  price: number;
  isComingSoon?: boolean;
  isFavorite?: boolean;
  virtualTourUrl?: string;
  colocationSlug: string;
  onWatchToggle: (colocationSlug: string) => Promise<boolean>;
}

const HouseCard: React.FC<HouseCardProps> = ({
  image,
  title,
  availableDate,
  price,
  isComingSoon = false,
  isFavorite = false,
  virtualTourUrl,
  colocationSlug,
  onWatchToggle,
}) => {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();
  const [isWatched, setIsWatched] = React.useState(isFavorite);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleWatchClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;
    setIsLoading(true);
    try {
      await onWatchToggle(colocationSlug);
      setIsWatched((prev) => !prev);
    } catch (error) {
      console.error('Error toggling watch status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = () => {
    window.location.href = `/${currentLocale}/shared-houses/${colocationSlug}`;
  };

  return (
    <Card
      sx={{
        width: '267px',
        borderRadius: 3,
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
      }}
      onClick={handleCardClick}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={image || placeholderImage}
          alt={title}
          sx={{
            objectFit: 'cover',
            borderRadius: 3,
            height: 220,
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const target = e.target as HTMLImageElement;
            target.src = placeholderImage;
          }}
        />

        {virtualTourUrl && (
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              zIndex: 10,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
            }}
          >
            <IconSaxIcon name="vr" size={18} strokeColor="none" />
          </Box>
        )}

        {/* Coming Soon Badge */}
        {isComingSoon && (
          <Box
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              zIndex: 10,
              padding: '6px 16px',
              borderRadius: 999,
              background:
                'linear-gradient(22.08deg, #F71818 -22.56%, #F9441B -9.98%, #FB7B1E 14.62%, #FEC600 58.21%, #FECA06 66.97%, #FED50E 75.17%, #FEE916 83.15%, #FFFF25 89.78%)',
            }}
            className="font-sofia-pro text-center text-[14px] font-medium leading-[21px] tracking-[0%] text-N900"
          >
            {t('js.components.house_card.coming_soon')}
          </Box>
        )}
      </Box>

      <Box className="mt-2 pt-4">
        <Box className="flex items-center justify-between">
          <Typography
            variant="h6"
            className="max-w-[200px] truncate font-sans text-[22px] font-bold"
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            title={title}
          >
            {title}
          </Typography>
          <Box
            onClick={handleWatchClick}
            sx={{
              cursor: isLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isLoading ? 0.7 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <IconSaxIcon name="heart" size={20} strokeColor={isWatched ? '#FECE1F' : '#1A1A1A'} />
          </Box>
        </Box>

        <Typography
          variant="body2"
          className="font-sans text-[14px] font-medium leading-[21px] text-N900"
        >
          {t('js.components.house_card.available_on')} {availableDate}
        </Typography>

        <Box sx={{ display: 'flex', mt: 1 }}>
          <Typography className="font-sans text-[14px] font-medium leading-[21px] text-N900">
            {t('js.components.colocation_card.price_from')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Typography className="font-sans text-4xl font-semibold text-N900">{price}</Typography>
            <Typography className="font-sans text-[14px] leading-[21px] text-N700">
              /{t('js.components.colocation_card.price_month')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export { HouseCard };
